import {
  AccountDeletionPreview,
  AccountDeletionReceipt,
  AccountExportResponse,
  AccountSwitchResponse,
  MeEntitlementSnapshot,
  ReconcilePurchaseClaimResponse,
  RestorePurchaseResponse,
  type AccountDeletionRetention,
  type AccountDeletionRetentionClass,
  type AccountSwitchClientResponsibility,
  type PurchaseClaimState,
} from '@signbento/contracts'
import type { LocalVault } from '@signbento/storage'
import type { MessageKey } from '@signbento/ui'
import { ApiError, type ApiTransport } from './api-client.js'
import { HOME_REQUESTS_CACHE_KEY } from './home-compartments.js'
import type { EntitlementSnapshot } from './purchases.js'

/**
 * Account lifecycle — S6.3 (PUR-04/05/15, RET-05, IDN-08, LOC-08, §2.7).
 * Signing out, switching accounts, restoring a purchase and deleting an account are the
 * paths where local data and account data must stay clearly separated: the device vault
 * is never silently attached to another account, a purchase restore never restores local
 * bytes, and deletion has a stated disposition for every class of data.
 */

/** Account-scoped local state that must be cleared when the identity changes (LOC-08). */
export const ACCOUNT_SCOPED_SETTING_KEYS = [
  'auth.pending_intent',
  'account.tokens',
  'account.summary',
  'account.entitlement_cache',
  'account.host_grants',
  'account.last_verified_at',
  // Home's last good read of the account's requests (titles and signer names).
  HOME_REQUESTS_CACHE_KEY,
] as const

export interface SwitchAccountOptions {
  readonly vault: LocalVault
  readonly from_account_id: string | null
  /** `null` signs out: account-scoped state is cleared and no account is current. */
  readonly to_account_id: string | null
  /** Attaching an existing local vault to another account is always explicit (§2.7). */
  readonly attach_local_vault: boolean
  readonly now: Date
}

export interface SwitchAccountResult {
  readonly cleared_keys: readonly string[]
  readonly vault_attached: boolean
  readonly local_records_preserved: true
}

export async function switchAccount(options: SwitchAccountOptions): Promise<SwitchAccountResult> {
  const cleared: string[] = []
  for (const key of ACCOUNT_SCOPED_SETTING_KEYS) {
    const existing = await options.vault.getSetting(key)
    if (existing === null) continue
    await options.vault.putSetting(key, null)
    cleared.push(key)
  }
  await options.vault.putSetting('account.current_id', options.to_account_id)
  await options.vault.putSetting('account.previous_id', options.from_account_id)
  if (options.attach_local_vault) {
    await options.vault.putSetting('account.vault_attached_at', options.now.toISOString())
  }
  return {
    cleared_keys: cleared,
    vault_attached: options.attach_local_vault,
    // Local documents, drafts, templates, signatures and completions are untouched.
    local_records_preserved: true,
  }
}

export function accountSwitchRequiresExplicitAttach(input: {
  readonly local_document_count: number
  readonly attach_local_vault: boolean
}): boolean {
  return input.local_document_count > 0 && !input.attach_local_vault
}

/**
 * What this device keeps for already-owned offline convenience: the service's own snapshot, plus
 * when it was read. The numbers are never re-shaped into a client's own balance — the snapshot
 * carries its `derived_at` and its `authority`, which is what lets a surface age it honestly and
 * stops it ever passing itself off as an authority (§2.7, S6.3).
 */
export interface EntitlementCache {
  readonly account_id: string
  /** When this device last had the service's answer; the offline window runs from here. */
  readonly verified_at: string
  /** When this device learned the entitlement was revoked, if it has. */
  readonly revocation_learned_at: string | null
  readonly entitlement: MeEntitlementSnapshot
}

export type OfflineEntitlementDecision =
  | { readonly kind: 'ALLOW_LOCAL_CONVENIENCE'; readonly until: string }
  | { readonly kind: 'EXPIRED' }
  | { readonly kind: 'REVOKED' }

export const OFFLINE_ENTITLEMENT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

/**
 * A previously verified entitlement may unlock already-owned local convenience while
 * offline, until it is revoked or the cache ages out — remote actions always check the
 * server (never the cache).
 */
export function offlineEntitlement(input: {
  readonly cache: EntitlementCache | null
  readonly account_id: string
  readonly now: Date
}): OfflineEntitlementDecision {
  const { cache } = input
  if (
    cache === null ||
    cache.account_id !== input.account_id ||
    !cache.entitlement.lifetime.active
  ) {
    return { kind: 'EXPIRED' }
  }
  if (cache.revocation_learned_at !== null) return { kind: 'REVOKED' }
  const verified = Date.parse(cache.verified_at)
  const age = input.now.getTime() - verified
  if (Number.isNaN(verified) || age > OFFLINE_ENTITLEMENT_MAX_AGE_MS) return { kind: 'EXPIRED' }
  return {
    kind: 'ALLOW_LOCAL_CONVENIENCE',
    until: new Date(verified + OFFLINE_ENTITLEMENT_MAX_AGE_MS).toISOString(),
  }
}

export interface RestorePlan {
  readonly steps: readonly (
    | 'reauthenticate'
    | 'reconcile_with_provider'
    | 'derive_entitlement_from_purchases'
    | 'refresh_credit_snapshot'
  )[]
  /** A restore never re-grants and never touches local documents. */
  readonly grants_new_entitlement: false
  readonly restores_local_documents: false
}

export function planPurchaseRestore(): RestorePlan {
  return {
    steps: [
      'reauthenticate',
      'reconcile_with_provider',
      'derive_entitlement_from_purchases',
      'refresh_credit_snapshot',
    ],
    grants_new_entitlement: false,
    restores_local_documents: false,
  }
}

export type ClaimReconciliation =
  | { readonly kind: 'KEEP_CURRENT_ACCOUNT' }
  | { readonly kind: 'MOVE_TO_CLAIMING_ACCOUNT'; readonly requires: 'reauth_and_confirmation' }
  | { readonly kind: 'DISCLOSE_NOTHING'; readonly reason: 'foreign_account' }

/**
 * A purchase already claimed by another account is reconciled explicitly: no double
 * grant, and no details about the other account leak (PUR-05).
 */
export function reconcileForeignClaim(input: {
  readonly purchase_account_id: string
  readonly current_account_id: string
}): ClaimReconciliation {
  if (input.purchase_account_id === input.current_account_id)
    return { kind: 'KEEP_CURRENT_ACCOUNT' }
  return { kind: 'MOVE_TO_CLAIMING_ACCOUNT', requires: 'reauth_and_confirmation' }
}

export interface DeletionPlan {
  readonly steps: readonly string[]
  readonly retention_disclosed: readonly MessageKey[]
  readonly local_files_outside_scope: true
}

/**
 * What deletion keeps, whatever the account holds. Message keys, not policy codes: a surface must
 * never render a machine code to a person (found in browser verification, where the raw codes
 * reached the Account section).
 */
export const DELETION_RETENTION_DISCLOSED: readonly MessageKey[] = [
  'settings.account_retention_financial',
  'settings.account_retention_audit',
  'settings.account_retention_sealed',
]

/** Account deletion disposition (RET-05): every class of data is named explicitly. */
export function planAccountDeletion(input: {
  readonly active_request_count: number
  readonly sealed_request_count: number
}): DeletionPlan {
  return {
    steps: [
      'reauthenticate',
      'stop_new_actions',
      'revoke_sessions_and_host_grants',
      ...(input.active_request_count > 0 ? ['void_active_requests_per_policy'] : []),
      ...(input.sealed_request_count > 0
        ? ['preserve_sealed_response_sets_until_lifecycle_end']
        : []),
      'delete_private_workspace_copies_and_indexes',
      'complete_deletion_without_support',
    ],
    retention_disclosed: DELETION_RETENTION_DISCLOSED,
    local_files_outside_scope: true,
  }
}

/** Account data export is reauthenticated and scoped; local files stay local. */
export function planAccountExport(): {
  readonly steps: readonly string[]
  readonly includes_local_files: false
} {
  return {
    steps: ['reauthenticate', 'collect_account_scoped_records', 'deliver_export', 'disclose_scope'],
    includes_local_files: false,
  }
}

/**
 * The deletion-scope sentence the UI shows, so no surface can imply local data is removed. The
 * copy itself lives in the catalogues (`settings.account_deletion_scope`), which is why this now
 * returns a key: an English literal in a library is an untranslated string by construction.
 */
export const DELETION_DISCLOSURE_KEY: MessageKey = 'settings.account_deletion_scope'

/* ==========================================================================
 * The client half of S6.3: the calls the surfaces make and what their answers mean.
 *
 * Every function here hands the surface a view built from the answer's own facts — never the
 * raw response — so a field the surface must not render (another account's identity, a policy
 * code) cannot reach it by accident.
 * ======================================================================== */

/** The local key holding the deletion receipt this device was given (ADD-007, RET-05). */
export const DELETION_RECEIPT_SETTING = 'account.deletion_receipt'

/**
 * The two ways a session stops being enough, kept apart because the copy differs: `reauth` is a
 * real session that is not *fresh* enough (the export, the deletion and a claim reconciliation
 * always ask for a fresh sign-in), and `sign_in` is no session at all.
 */
export type AuthOutcome = { readonly kind: 'AUTH_REQUIRED'; readonly reason: 'reauth' | 'sign_in' }

function authOutcome(error: unknown): AuthOutcome | null {
  if (!(error instanceof ApiError)) return null
  if (error.code === 'OP_REAUTHENTICATION_REQUIRED') {
    return { kind: 'AUTH_REQUIRED', reason: 'reauth' }
  }
  if (error.code === 'OP_UNAUTHENTICATED') return { kind: 'AUTH_REQUIRED', reason: 'sign_in' }
  return null
}

function failureCode(error: unknown): string {
  return error instanceof ApiError && error.code.length > 0 ? error.code : 'OP_UNEXPECTED'
}

/* ------------------------------ restore (PUR-04) ------------------------------ */

export type RestoreOutcome =
  | {
      readonly kind: 'RESTORED' | 'NOTHING_TO_RESTORE'
      /** The snapshot the service re-derived, when it answered with one. */
      readonly snapshot: EntitlementSnapshot | null
      /**
       * False when no provider adapter is wired in this deployment: the answer then comes only
       * from the service's own records and says so instead of implying a round-trip it did not
       * make.
       */
      readonly provider_checked: boolean
      readonly provider_state: 'PENDING' | 'PURCHASED' | 'REVOKED' | 'NOTHING_TO_VERIFY' | null
    }
  | AuthOutcome
  | { readonly kind: 'FAILED'; readonly code: string }

/**
 * Restore Purchase / Refresh Entitlement. Recovers the account's rights and credits from the
 * service's own verified records — it grants nothing new and brings back no local document, and
 * the surface's copy says both (PUR-04, ADD-027/IDN-08).
 */
export async function restoreAccountPurchases(options: {
  readonly transport: ApiTransport
  readonly id_factory: () => string
}): Promise<RestoreOutcome> {
  const client_request_id = options.id_factory()
  try {
    const payload = await options.transport.request<unknown>({
      method: 'POST',
      path: '/v1/me/purchases/restore',
      body: { client_request_id },
      client_request_id,
    })
    const response = RestorePurchaseResponse.safeParse(payload)
    if (!response.success) return { kind: 'FAILED', code: 'OP_INVALID_PAYLOAD' }
    return {
      kind: response.data.restored ? 'RESTORED' : 'NOTHING_TO_RESTORE',
      snapshot: response.data.entitlement ?? null,
      provider_checked: response.data.reconciliation.provider_checked,
      provider_state: response.data.reconciliation.provider_state,
    }
  } catch (error) {
    return authOutcome(error) ?? { kind: 'FAILED', code: failureCode(error) }
  }
}

/* --------------------------- claim reconciliation (PUR-05) --------------------------- */

/** What the person's reference is: a store receipt, or the checkout the provider returned. */
export type ClaimLocatorKind = 'store_receipt' | 'checkout_session'

export interface ClaimLocator {
  readonly provider_transaction_id?: string
  readonly session_id?: string
}

/**
 * Turns what a person has into the locator the service looks up, or refuses locally: a claim with
 * neither locator could not be looked up at all, and asking anyway would be a guess.
 */
export function claimLocator(input: {
  readonly kind: ClaimLocatorKind
  readonly reference: string
}): ClaimLocator | null {
  const reference = input.reference.trim()
  if (reference.length === 0) return null
  return input.kind === 'store_receipt'
    ? { provider_transaction_id: reference }
    : { session_id: reference }
}

export type ClaimOutcome =
  | {
      readonly kind: Exclude<PurchaseClaimState, 'CLAIMED_BY_ANOTHER_ACCOUNT'>
      readonly owned_by_current_account: boolean
      readonly provider_checked: boolean
      /** The calling account's own snapshot, re-derived; a foreign claim never carries another's. */
      readonly snapshot: EntitlementSnapshot | null
    }
  | {
      readonly kind: 'CLAIMED_BY_ANOTHER_ACCOUNT'
      /** The only move there is: sign in as that account and confirm there. */
      readonly requires: 'reauth_and_confirmation'
      readonly provider_checked: boolean
      readonly snapshot: EntitlementSnapshot | null
    }
  | AuthOutcome
  | { readonly kind: 'INVALID_CLAIM' }
  | { readonly kind: 'FAILED'; readonly code: string }

/**
 * Checks what a presented transaction reconciles to. A claim that belongs to another account is
 * answered with its class and the move only: this function builds that view itself and drops
 * everything else the answer carried, so no surface can render another account's details (PUR-05).
 */
export async function reconcileAccountClaim(options: {
  readonly transport: ApiTransport
  readonly claim: ClaimLocator
  readonly id_factory: () => string
}): Promise<ClaimOutcome> {
  const client_request_id = options.id_factory()
  try {
    const payload = await options.transport.request<unknown>({
      method: 'POST',
      path: '/v1/me/purchases/reconcile',
      body: { client_request_id, ...options.claim },
      client_request_id,
    })
    const answer = ReconcilePurchaseClaimResponse.safeParse(payload)
    if (!answer.success) return { kind: 'FAILED', code: 'OP_INVALID_PAYLOAD' }
    const snapshot = answer.data.entitlement
    if (answer.data.state === 'CLAIMED_BY_ANOTHER_ACCOUNT') {
      // Only the class and the move: everything else the answer carried is dropped here, so no
      // surface can render another account's details.
      return {
        kind: 'CLAIMED_BY_ANOTHER_ACCOUNT',
        requires: 'reauth_and_confirmation',
        provider_checked: answer.data.provider_checked,
        snapshot,
      }
    }
    return {
      kind: answer.data.state,
      owned_by_current_account: answer.data.owned_by_current_account,
      provider_checked: answer.data.provider_checked,
      snapshot,
    }
  } catch (error) {
    const auth = authOutcome(error)
    if (auth !== null) return auth
    const code = failureCode(error)
    // A claim with no usable locator is the service's own invalid-payload answer; the surface
    // says what to enter rather than reporting an outage.
    return code === 'OP_INVALID_PAYLOAD' ? { kind: 'INVALID_CLAIM' } : { kind: 'FAILED', code }
  }
}

/* ------------------------------ switch (LOC-08) ------------------------------ */

/** What the service enforces when the identity changes, and what only the client can do. */
export const ACCOUNT_SWITCH_RESPONSIBILITY_KEYS: Readonly<
  Record<AccountSwitchClientResponsibility, MessageKey>
> = {
  CLEAR_ACCOUNT_SCOPED_LOCAL_STATE: 'settings.account_switch_clear_local',
  KEEP_DEVICE_LOCAL_VAULT: 'settings.account_switch_keep_vault',
  ATTACH_EXISTING_VAULT_EXPLICITLY: 'settings.account_switch_attach_explicit',
}

export type SwitchOutcome =
  | { readonly kind: 'SWITCHED'; readonly view: AccountSwitchResponse }
  | AuthOutcome
  | { readonly kind: 'FAILED'; readonly code: string }

/**
 * Stops the old identity's uncommitted actions and revokes the session that presented the call.
 * The local half — clearing account-scoped state and deciding whether to attach the vault — stays
 * with `switchAccount` below, because the vault is the client's (§2.7, LOC-08).
 */
export async function switchAccountIdentity(options: {
  readonly transport: ApiTransport
  readonly id_factory: () => string
}): Promise<SwitchOutcome> {
  const client_request_id = options.id_factory()
  try {
    const payload = await options.transport.request<unknown>({
      method: 'POST',
      path: '/v1/me/account/switch',
      body: { client_request_id },
      client_request_id,
    })
    const answer = AccountSwitchResponse.safeParse(payload)
    if (!answer.success) return { kind: 'FAILED', code: 'OP_INVALID_PAYLOAD' }
    return { kind: 'SWITCHED', view: answer.data }
  } catch (error) {
    return authOutcome(error) ?? { kind: 'FAILED', code: failureCode(error) }
  }
}

/* ------------------------------- export (§13.5) ------------------------------- */

export type ExportOutcome =
  | { readonly kind: 'EXPORTED'; readonly view: AccountExportResponse }
  | AuthOutcome
  | { readonly kind: 'FAILED'; readonly code: string }

/**
 * The reauthenticated account/remote-data export. The body is handed straight to the caller to
 * download — the service does not store it — and the view keeps the scope literals and the bounds,
 * so the surface says what the export covers and where it stopped (§13.5, AT-022).
 */
export async function exportAccountData(options: {
  readonly transport: ApiTransport
  readonly id_factory: () => string
}): Promise<ExportOutcome> {
  const client_request_id = options.id_factory()
  try {
    const payload = await options.transport.request<unknown>({
      method: 'POST',
      path: '/v1/me/account/export',
      body: { client_request_id },
      client_request_id,
    })
    const exportBundle = AccountExportResponse.safeParse(payload)
    if (!exportBundle.success) return { kind: 'FAILED', code: 'OP_INVALID_PAYLOAD' }
    return { kind: 'EXPORTED', view: exportBundle.data }
  } catch (error) {
    return authOutcome(error) ?? { kind: 'FAILED', code: failureCode(error) }
  }
}

/* ------------------------------ deletion (RET-05) ------------------------------ */

export const ACCOUNT_DELETION_RETENTION_KEYS: Readonly<
  Record<AccountDeletionRetentionClass, MessageKey>
> = {
  FINANCIAL_RECORDS: 'settings.account_retention_financial',
  MINIMIZED_SECURITY_EVIDENCE: 'settings.account_retention_audit',
  SENT_REQUESTS: 'settings.account_retention_sealed',
}

/**
 * How long a retained class is kept: a bounded window, the record's own lifecycle, or `null` when
 * the class's own sentence already says why it is kept and promises no end date (financial records
 * and the minimized log, which nothing purges yet). Those two never show a window, even on a
 * receipt issued before this wording that still carries one: no end date is shown that nothing
 * enforces.
 */
export function retentionWindowKey(retention: AccountDeletionRetention): MessageKey | null {
  if (retention.retention_class !== 'SENT_REQUESTS') return null
  return retention.window_days === null
    ? 'settings.account_retention_lifecycle'
    : 'settings.account_retention_days'
}

export type DeletionOutcome =
  | { readonly kind: 'COMPLETED'; readonly receipt: AccountDeletionReceipt }
  | AuthOutcome
  | { readonly kind: 'FAILED'; readonly code: string }

/**
 * Deletes the account through the in-app route: a typed `DELETE` plus a fresh sign-in. A retry
 * presents the receipt it was given, which is what makes a repeated attempt read as the same
 * completed deletion rather than a second purge or an error (RET-05, ADD-007).
 */
export async function deleteAccountWithReceipt(options: {
  readonly transport: ApiTransport
  readonly id_factory: () => string
  readonly receipt_id?: string
}): Promise<DeletionOutcome> {
  const client_request_id = options.id_factory()
  try {
    const payload = await options.transport.request<unknown>({
      method: 'POST',
      path: '/v1/me/account/delete',
      body: {
        client_request_id,
        confirmation: 'DELETE',
        ...(options.receipt_id === undefined ? {} : { receipt_id: options.receipt_id }),
      },
      client_request_id,
    })
    const receipt = AccountDeletionReceipt.safeParse(payload)
    if (!receipt.success) return { kind: 'FAILED', code: 'OP_INVALID_PAYLOAD' }
    return { kind: 'COMPLETED', receipt: receipt.data }
  } catch (error) {
    return authOutcome(error) ?? { kind: 'FAILED', code: failureCode(error) }
  }
}

export type DeletionPreviewOutcome =
  | { readonly kind: 'READY'; readonly preview: AccountDeletionPreview }
  | AuthOutcome
  | { readonly kind: 'FAILED'; readonly code: string }

/**
 * Reads what deleting the account would do to its requests (`/v1/me/account/deletion-preview`): the
 * same split the deletion acts on and its receipt reports.
 */
export async function loadDeletionPreview(
  transport: ApiTransport,
): Promise<DeletionPreviewOutcome> {
  try {
    const payload = await transport.request<unknown>({
      method: 'GET',
      path: '/v1/me/account/deletion-preview',
    })
    const preview = AccountDeletionPreview.safeParse(payload)
    if (!preview.success) return { kind: 'FAILED', code: 'OP_INVALID_PAYLOAD' }
    return { kind: 'READY', preview: preview.data }
  } catch (error) {
    return authOutcome(error) ?? { kind: 'FAILED', code: failureCode(error) }
  }
}

/** One line of the deletion disclosure about the account's requests. */
export type DeletionRequestLine =
  | { readonly kind: 'CHECKING' }
  | { readonly kind: 'UNKNOWN' }
  | { readonly kind: 'VOID_ACTIVE'; readonly count: number }
  | { readonly kind: 'KEEP_SEALED'; readonly count: number }
  | { readonly kind: 'NONE' }

/**
 * The request lines of the deletion disclosure, from the plan built with the account's real counts
 * (`null` while they are being read). A count that could not be read says so and states the policy
 * instead; it is never shown as "no requests".
 */
export function deletionRequestLines(
  preview: DeletionPreviewOutcome | null,
): readonly DeletionRequestLine[] {
  if (preview === null) return [{ kind: 'CHECKING' }]
  if (preview.kind !== 'READY') return [{ kind: 'UNKNOWN' }]
  const counts = preview.preview
  const plan = planAccountDeletion(counts)
  const lines: DeletionRequestLine[] = []
  if (plan.steps.includes('void_active_requests_per_policy')) {
    lines.push({ kind: 'VOID_ACTIVE', count: counts.active_request_count })
  }
  if (plan.steps.includes('preserve_sealed_response_sets_until_lifecycle_end')) {
    lines.push({ kind: 'KEEP_SEALED', count: counts.sealed_request_count })
  }
  return lines.length > 0 ? lines : [{ kind: 'NONE' }]
}

/** The receipt this device holds, with the account it was made under. */
export interface StoredDeletionReceipt {
  readonly account_id: string | null
  readonly receipt: AccountDeletionReceipt
}

/**
 * Reads the receipt this device stored. Storage is untrusted input, so the receipt is parsed with
 * the contract's own schema: anything that does not carry every field a surface renders is no
 * receipt at all, and a half-read receipt would let a surface claim a deletion it cannot show.
 */
export function readDeletionReceipt(raw: unknown): StoredDeletionReceipt | null {
  if (typeof raw !== 'object' || raw === null) return null
  if (!('receipt' in raw) || !('account_id' in raw)) return null
  const accountId = raw.account_id
  if (accountId !== null && typeof accountId !== 'string') return null
  const receipt = AccountDeletionReceipt.safeParse(raw.receipt)
  if (!receipt.success) return null
  return { account_id: accountId, receipt: receipt.data }
}

/* ------------------- offline convenience, honestly stated (§2.7) ------------------- */

/**
 * Reads the snapshot this device cached for offline convenience. The vault is untrusted input, so
 * the snapshot is parsed with the contract's own schema: a value without the service's `derived_at`
 * and its `authority` statement is no cache at all, because this device could not then say how old
 * the numbers are — and an age it cannot state is an age it must not guess.
 */
export function readEntitlementCache(raw: unknown): EntitlementCache | null {
  if (typeof raw !== 'object' || raw === null) return null
  if (!('account_id' in raw) || !('verified_at' in raw) || !('entitlement' in raw)) return null
  const accountId = raw.account_id
  const verifiedAt = raw.verified_at
  const learnedAt = 'revocation_learned_at' in raw ? raw.revocation_learned_at : null
  if (typeof accountId !== 'string' || typeof verifiedAt !== 'string') return null
  if (learnedAt !== null && typeof learnedAt !== 'string') return null
  const entitlement = MeEntitlementSnapshot.safeParse(raw.entitlement)
  if (!entitlement.success) return null
  return {
    account_id: accountId,
    verified_at: verifiedAt,
    revocation_learned_at: learnedAt,
    entitlement: entitlement.data,
  }
}

/**
 * Writes the snapshot the service just answered with, so already-owned local convenience keeps
 * working offline. The service's own `derived_at` is what the surface ages from, so a snapshot
 * that arrives without one is not cached rather than cached with a guessed age.
 */
export async function rememberEntitlement(input: {
  readonly vault: LocalVault
  readonly account_id: string
  readonly snapshot: EntitlementSnapshot
  readonly now: Date
}): Promise<EntitlementCache | null> {
  const snapshot = MeEntitlementSnapshot.safeParse(input.snapshot)
  if (!snapshot.success) return null
  const cache: EntitlementCache = {
    account_id: input.account_id,
    verified_at: input.now.toISOString(),
    // A snapshot that already says revoked is one this device has just learned about.
    revocation_learned_at:
      snapshot.data.lifetime.revoked_at === null ? null : input.now.toISOString(),
    entitlement: snapshot.data,
  }
  await input.vault.putSetting('account.entitlement_cache', cache)
  await input.vault.putSetting('account.last_verified_at', cache.verified_at)
  return cache
}

export interface EntitlementCacheAge {
  readonly derived_at: string
  readonly age_ms: number
  readonly days: number
  readonly message_key:
    | 'settings.account_cache_age_today'
    | 'settings.account_cache_age_one'
    | 'settings.account_cache_age'
  /** A cached snapshot is a convenience and can never be an authority (§2.7). */
  readonly is_authority: false
}

/** How old the cached numbers are, from the service's own derivation time. */
export function entitlementCacheAge(input: {
  readonly cache: EntitlementCache
  readonly now: Date
}): EntitlementCacheAge | null {
  const derived_at = input.cache.entitlement.derived_at
  const derived = Date.parse(derived_at)
  if (Number.isNaN(derived)) return null
  const age_ms = Math.max(0, input.now.getTime() - derived)
  const days = Math.floor(age_ms / (24 * 60 * 60 * 1000))
  return {
    derived_at,
    age_ms,
    days,
    message_key:
      days === 0
        ? 'settings.account_cache_age_today'
        : days === 1
          ? 'settings.account_cache_age_one'
          : 'settings.account_cache_age',
    is_authority: false,
  }
}

/**
 * ADD-027: an empty local vault is not "no documents". On a new device the vault is empty while
 * the account holds remote records, and the surface must say exactly that instead of implying
 * there is nothing to recover.
 */
export function localVaultLabel(input: {
  readonly local_document_count: number
  readonly account_connected: boolean
  /** Remote records the service has reported for this account (0 when it has not answered). */
  readonly account_remote_records: number
}): MessageKey {
  if (input.local_document_count === 1) return 'settings.account_vault_documents_one'
  if (input.local_document_count > 0) return 'settings.account_vault_documents'
  if (!input.account_connected) return 'settings.account_vault_empty'
  return input.account_remote_records > 0
    ? 'settings.account_vault_empty_remote'
    : 'settings.account_vault_empty_connected'
}
